import { useEffect, useRef } from 'react';
import { porVencer, vencimientoDeCasos } from './firmas';
import type { Caso } from './tipos';

/**
 * Renueva las URLs firmadas (duran 1 h) ANTES de que venzan: al montar y cada
 * `intervaloMs` mira el vencimiento de `casos` y, si está dentro del margen, llama
 * `refrescar()` — una sola vez por tanda y vencimiento (se recuerda el `exp` renovado
 * JUNTO con la tanda que lo tenía, así un refresco que no cambie las URLs no se repite
 * cada minuto). Guard de re-entrancia por ref: un refresco que tarda más que un tick no
 * se pisa. Si falla, se loguea y se reintenta en el próximo tick. `casos` y `refrescar`
 * se leen por ref para no reiniciar el intervalo en cada render (el caller define
 * `refrescar` inline).
 */
export function useRefrescoFirmas(casos: Caso[], refrescar: () => Promise<void>, intervaloMs = 60_000): void {
  const casosRef = useRef(casos);
  const refrescarRef = useRef(refrescar);
  const enCursoRef = useRef(false);
  /** Última renovación: el `exp` Y la tanda (identidad del array) que lo tenía. */
  const renovadoRef = useRef<{ exp: number; casos: Caso[] } | null>(null);

  useEffect(() => {
    casosRef.current = casos;
  }, [casos]);
  useEffect(() => {
    refrescarRef.current = refrescar;
  }, [refrescar]);

  useEffect(() => {
    const chequear = async () => {
      if (enCursoRef.current) return;
      const casos = casosRef.current;
      const exp = vencimientoDeCasos(casos);
      if (exp === null || !porVencer(exp)) return;
      // "Ya renovado" vale solo para ESTA tanda: el `exp` tiene granularidad de segundo y
      // dos tandas firmadas en el mismo segundo lo comparten. Marcar por `exp` solo dejaba
      // sin renovar a una tanda nueva con el mismo vencimiento (p. ej. una corrida que
      // vuelve del caché de Comparar) cuando un refresco anterior —aunque el caller lo
      // haya descartado porque la selección cambió— ya lo había anotado.
      const r = renovadoRef.current;
      if (r && r.exp === exp && r.casos === casos) return;
      enCursoRef.current = true;
      try {
        await refrescarRef.current();
        renovadoRef.current = { exp, casos };
      } catch (e) {
        console.error('[analisis] renovar URLs firmadas falló (se reintenta en el próximo tick):', e);
      } finally {
        enCursoRef.current = false;
      }
    };
    void chequear();
    const timer = setInterval(() => void chequear(), intervaloMs);
    return () => clearInterval(timer);
  }, [intervaloMs]);
}
